import { Component, ChangeDetectionStrategy, inject, computed, AfterViewInit, viewChild, signal } from '@angular/core';

import { RouterModule, RouterOutlet } from '@angular/router';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UserButtonComponent } from "./user-button/user-button.component";
import { StoreSelectionComponent } from "./store-selection/store-selection.component";
import { OrderService } from '../orders/order.service';
import { FooterComponent } from '../shared/footer/footer.component';
import { CustomerService } from '../core/customer.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import { M3Breakpoints } from '../shared/breakpoints';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { map, of } from 'rxjs';
import { MatBadgeModule } from '@angular/material/badge';
import { FeedbackButtonComponent } from "./feedback-button/feedback-button.component";
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { AppSettingsDialogComponent } from '../core/dialogs/app-settings-dialog/app-settings-dialog.component';
import { OnboardingService } from '../core/services/onboarding.service';
import { OnboardingComponent } from '../onboarding/onboarding.component';
import { SessionService } from '../session/session.service';
import { CreditClarificationList, OrderRepository, PosCompensationCounts, PosSettings, UserType } from 'sp-client';
import { PosSettingsRepository } from 'sp-client';
import { PosCompensationBadgeService } from '../pos-compensation/pos-compensation-badge.service';

const EMPTY_POS_COMPENSATION_COUNTS: PosCompensationCounts = {
  all: 0,
  awaiting: 0,
  sync_error: 0,
  ack_unclear: 0,
  manual_review: 0,
  bonus_issue: 0,
  refund_required: 0,
  resolved: 0,
};

const EMPTY_CREDIT_CLARIFICATION_LIST: CreditClarificationList = {
  items: [],
  total: 0,
};
interface NavSection {
  nameKey?: string;
  items: NavItem[];
}

interface NavItem {
  nameKey: string;
  route: string;
  icon: string;
  count?: () => number | undefined;
  visible?: () => boolean;
}

@Component({
  selector: 'sp-main-layout',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    RouterModule,
    RouterOutlet,
    MatSidenavModule,
    MatListModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    TranslateModule,
    MatTooltipModule,
    UserButtonComponent,
    StoreSelectionComponent,
    FooterComponent,
    MatBadgeModule,
    FeedbackButtonComponent,
    MatDialogModule
],
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss'
})
export class MainLayoutComponent implements AfterViewInit {
  readonly navSections: NavSection[] = [
    {
      items: [

        { nameKey: 'mainLayout.navDashboard', route: 'dashboard', icon: 'dashboard' },
      ],
    },
    {
      nameKey: 'mainLayout.navCatalog',
      items: [
        { nameKey: 'mainLayout.navProducts', route: 'products', icon: 'inventory' },
        { nameKey: 'mainLayout.navMedia', route: 'media', icon: 'photo_library', visible: () => !this.posSettingsResource.value()?.posIntegrationEnabled },
      ],
    },
    {
      nameKey: 'mainLayout.navSales',
      items: [
        { nameKey: 'mainLayout.navOrders', route: 'orders', icon: 'shopping_cart', count: () => this.ordersCount() },
        { nameKey: 'mainLayout.navInvoices', route: 'invoices', icon: 'receipt', visible: () => this.canAccessInvoices(), },
        { nameKey: 'mainLayout.navStatistics', route: 'statistics', icon: 'bar_chart' },
      ],
    },
    {
      nameKey: 'mainLayout.navCustomers',
      items: [
        { nameKey: 'mainLayout.navLoyaltyProgram', route: 'loyalty-program', icon: 'redeem', visible: () => this.canAccessLoyaltyProgram()},
        { nameKey: 'mainLayout.navCustomerCards', route: 'customer-cards', icon: 'credit_card' },
        { nameKey: 'mainLayout.navPushNotifications', route: 'push-notifications', icon: 'notifications' },
        { nameKey: 'mainLayout.navPromoCodes', route: 'promo-codes', icon: 'local_offer', visible: () => this.canAccessPromoCodes() },
      ],
    },
    {
      nameKey: 'mainLayout.navManagement',
      items: [
        { nameKey: 'mainLayout.navOptions', route: 'options', icon: 'tune' },
        { nameKey: 'mainLayout.navFilters', route: 'filters', icon: 'filter_list' },
        {
          nameKey: 'mainLayout.navPosCompensation',
          route: 'pos-compensation',
          icon: 'sync_problem',
          count: () => this.posCompensationCount(),
          visible: () => this.shouldShowPosCompensationNav(),
        },
        { nameKey: 'mainLayout.navLogs', route: 'logs', icon: 'list_alt', visible: () => this.canAccessLogs() },
      ],
    }
  ];

  private readonly orderService = inject(OrderService);
  private readonly customerService = inject(CustomerService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly dialog = inject(MatDialog);
  private readonly onboardingService = inject(OnboardingService);
  private readonly sessionService = inject(SessionService);
  private readonly orderRepository = inject(OrderRepository);
  private readonly posCompensationBadgeService = inject(PosCompensationBadgeService);
  private posSettingsRepository = inject(PosSettingsRepository);
  protected readonly posSettingsResource = rxResource<PosSettings, void>({
    stream: () => this.posSettingsRepository.getSettings(),
  });
  readonly ordersCount = computed(() => {
    if (this.orderService.activeOrdersCountResource.status() === 'error') return 0;
    const count = this.orderService.activeOrdersCountResource.value();
    if (!count || count < 1) return undefined;
    return count;
  });

  protected readonly customerName = computed(() => {
    if (this.customerService.clients.status() === 'error') return undefined;
    return this.customerService.clients.value()?.[0]?.company;
  });
  protected readonly shouldOverlay = toSignal(this.breakpointObserver.observe([M3Breakpoints.Compact, M3Breakpoints.Medium]).pipe(map((state) => state.matches)));

  protected readonly canAccessInvoices = computed(() => this.sessionService.user()?.user_type_id === UserType.BackendAdmin);
  protected readonly canAccessLogs = computed(() => this.sessionService.user()?.user_type_id === UserType.BackendAdmin);
  protected readonly canAccessLoyaltyProgram = computed(() => this.sessionService.user()?.user_type_id === UserType.BackendAdmin);
  protected readonly canAccessPromoCodes = computed(() => this.sessionService.user()?.user_type_id === UserType.BackendAdmin || this.sessionService.user()?.user_type_id === UserType.BackendUser);
  protected readonly canAccessPosCompensation = computed(() => this.sessionService.user()?.user_type_id === UserType.BackendAdmin);
  protected readonly posCompensationCountsResource = rxResource<PosCompensationCounts, { enabled: boolean; refresh: number }>({
    params: () => ({
      enabled: this.canAccessPosCompensation(),
      refresh: this.posCompensationBadgeService.refreshTick(),
    }),
    stream: ({ params }) =>
      params.enabled ? this.orderRepository.getPosCompensationCounts() : of(EMPTY_POS_COMPENSATION_COUNTS),
  });
  protected readonly creditClarificationResource = rxResource<CreditClarificationList, { enabled: boolean; refresh: number }>({
    params: () => ({
      enabled: this.canAccessPosCompensation(),
      refresh: this.posCompensationBadgeService.refreshTick(),
    }),
    stream: ({ params }) =>
      params.enabled ? this.orderRepository.getCreditClarificationCases() : of(EMPTY_CREDIT_CLARIFICATION_LIST),
  });
  protected readonly posCompensationCount = computed(() => {
    if (!this.canAccessPosCompensation()) return undefined;
    if (this.posCompensationCountsResource.status() === 'error' && this.creditClarificationResource.status() === 'error') {
      return undefined;
    }
    const count =
      (this.posCompensationCountsResource.value()?.all ?? 0) +
      (this.creditClarificationResource.value()?.total ?? this.creditClarificationResource.value()?.items.length ?? 0);
    return count > 0 ? count : undefined;
  });
  protected readonly hasOpenPosCompensationCases = computed(() => (this.posCompensationCount() ?? 0) > 0);
  protected readonly hasPosCompensationHistory = computed(
    () => (this.posCompensationCountsResource.value()?.resolved ?? 0) > 0,
  );
  protected readonly shouldShowPosCompensationNav = computed(() => {
    if (!this.canAccessPosCompensation()) return false;

    return (
      this.posSettingsResource.value()?.posIntegrationEnabled === true ||
      this.hasOpenPosCompensationCases() ||
      this.hasPosCompensationHistory()
    );
  });
  sidenav = viewChild<MatSidenav>('sidenav');

  protected isSidenavOpen = signal(false);
  protected readonly isContentWrapped = computed(() => !this.shouldOverlay() && this.isSidenavOpen());

  ngAfterViewInit(): void {
    this.sidenav()?.openedChange.subscribe((opened) => {
      this.isSidenavOpen.set(opened);
    });
    this.isSidenavOpen.set(this.sidenav()?.opened ?? false);
    this.showOnboardingIfNeeded();
  }

  constructor() {
    // TODO(yuna): automatically close the sidenav if viewport becomes compact
  }

  openAppSettingsDialog(): void {
    this.dialog.open(AppSettingsDialogComponent, {
      width: '500px', // Example width, adjust as needed
    });
  }

  private showOnboardingIfNeeded(): void {
    if (this.onboardingService.shouldShowOnboarding()) {
      this.dialog.open(OnboardingComponent, {
        width: '600px', // As per SCSS max-width
        maxWidth: '90vw',
        disableClose: true, // User must explicitly skip or finish
        autoFocus: 'dialog',
      });
    }
  }

}
